import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import { getSalesMonthly } from '../services/DashboardServices';
import './MarketingSalesChart.css';

Chart.register(ChartDataLabels);

const BAR_COLORS = [
  '#3498db', '#9b59b6', '#1abc9c', '#f39c12', '#e74c3c',
  '#2ecc71', '#34495e', '#e67e22', '#16a085', '#8e44ad',
];

const VIEW_OPTIONS = [
  { id: 'marketing', label: 'Marketing-wise' },
  { id: 'product', label: 'Product-wise' },
  { id: 'delivery', label: 'Delivery mode' },
  { id: 'vehicle', label: 'Vehicle-wise' },
  { id: 'month', label: 'Month-wise' },
  { id: 'day', label: 'Day-wise' },
];

function parseSalesDate(value) {
  if (!value) return null;
  const parsed = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
  return parsed.isValid() ? parsed : null;
}

/** Indian FY starts in April (month index 3). */
export function getFinancialYearStartYear(refMoment) {
  const m = moment(refMoment);
  if (!m.isValid()) {
    const now = moment();
    return now.month() >= 3 ? now.year() : now.year() - 1;
  }
  return m.month() >= 3 ? m.year() : m.year() - 1;
}

export function getFinancialYearBounds(startYear) {
  const fyStart = moment({ year: startYear, month: 3, day: 1 }).startOf('day');
  const fyEnd = moment({ year: startYear + 1, month: 2, day: 1 }).endOf('month');
  const label = `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
  return { fyStart, fyEnd, label, startYear };
}

function buildFinancialYearOptions(refMoment) {
  const currentStart = getFinancialYearStartYear(refMoment);
  return [currentStart - 1, currentStart, currentStart + 1].map((startYear) => {
    const bounds = getFinancialYearBounds(startYear);
    return { startYear, label: bounds.label };
  });
}

/**
 * Month-wise rows for one FY: Apr → Mar (include zero months).
 * Accepts either DC sales rows ({date, qty}) or monthly API rows ({month, qty}).
 */
export function aggregateMonthWiseByFinancialYear(salesOrMonthly, fyStartYear) {
  const { fyStart, fyEnd, label } = getFinancialYearBounds(fyStartYear);
  const months = [];
  for (let i = 0; i < 12; i += 1) {
    const monthMoment = fyStart.clone().add(i, 'months');
    months.push({
      key: monthMoment.format('YYYY-MM'),
      name: monthMoment.format('MMM'),
      qty: 0,
      sortKey: monthMoment.format('YYYY-MM'),
    });
  }

  const indexByKey = Object.fromEntries(months.map((row, index) => [row.key, index]));
  (salesOrMonthly || []).forEach((row) => {
    let key = null;
    let qty = Number(row.qty) || 0;
    if (row.month) {
      key = String(row.month).slice(0, 7);
    } else {
      const dt = parseSalesDate(row.date);
      if (!dt || dt.isBefore(fyStart, 'day') || dt.isAfter(fyEnd, 'day')) return;
      key = dt.format('YYYY-MM');
    }
    const index = indexByKey[key];
    if (index == null) return;
    months[index].qty = Number((months[index].qty + qty).toFixed(2));
  });

  return { rows: months, fyLabel: label, fyStart, fyEnd };
}

function bucketTopN(rows, topN = 8) {
  if (rows.length <= topN) return rows;
  const top = rows.slice(0, topN);
  const othersQty = rows.slice(topN).reduce((sum, row) => sum + row.qty, 0);
  if (othersQty > 0) {
    top.push({ name: 'Others', qty: Number(othersQty.toFixed(2)), sortKey: null });
  }
  return top;
}

function aggregateByKey(salesData, keyFn, { sortByQty = true, topN = null } = {}) {
  const totals = {};
  const meta = {};
  (salesData || []).forEach((row) => {
    const info = keyFn(row);
    if (!info) return;
    const { key, label, sortKey } = info;
    totals[key] = (totals[key] || 0) + (Number(row.qty) || 0);
    if (!meta[key]) {
      meta[key] = { label, sortKey };
    }
  });

  let rows = Object.entries(totals).map(([key, qty]) => ({
    name: meta[key].label,
    qty: Number(qty.toFixed(2)),
    sortKey: meta[key].sortKey,
  }));

  if (sortByQty) {
    rows.sort((a, b) => b.qty - a.qty);
  } else {
    rows.sort((a, b) => {
      if (a.sortKey == null && b.sortKey == null) return 0;
      if (a.sortKey == null) return 1;
      if (b.sortKey == null) return -1;
      if (a.sortKey < b.sortKey) return -1;
      if (a.sortKey > b.sortKey) return 1;
      return 0;
    });
  }

  if (topN) {
    rows = bucketTopN(rows, topN);
  }
  return rows;
}

export function aggregateSalesByView(salesData, view) {
  if (view === 'product') {
    return aggregateByKey(
      salesData,
      (row) => {
        const name = String(row.grade || '').trim() || 'Unspecified';
        return { key: name, label: name, sortKey: name };
      },
      { sortByQty: true, topN: 8 },
    );
  }

  if (view === 'delivery') {
    return aggregateByKey(
      salesData,
      (row) => {
        const name = String(row.delivery_mode || '').trim().toUpperCase() || 'UNKNOWN';
        return { key: name, label: name, sortKey: name };
      },
      { sortByQty: true },
    );
  }

  if (view === 'vehicle') {
    return aggregateByKey(
      salesData,
      (row) => {
        const name = String(row.vehiclenop || '').trim() || 'Unassigned';
        return { key: name, label: name, sortKey: name };
      },
      { sortByQty: true },
    );
  }

  if (view === 'day') {
    return aggregateByKey(
      salesData,
      (row) => {
        const dt = parseSalesDate(row.date);
        if (!dt) return null;
        return {
          key: dt.format('YYYY-MM-DD'),
          label: dt.format('DD MMM'),
          sortKey: dt.format('YYYY-MM-DD'),
        };
      },
      { sortByQty: false },
    );
  }

  // default: marketing-wise
  return aggregateByKey(
    salesData,
    (row) => {
      const name = String(row.marketing || '').trim() || 'Unassigned';
      return { key: name, label: name, sortKey: name };
    },
    { sortByQty: true, topN: 8 },
  );
}

function getViewMeta(view, fyLabel) {
  switch (view) {
    case 'product':
      return {
        title: 'Product-wise Sales Performance',
        subtitle: 'Delivered qty by grade (m³)',
      };
    case 'delivery':
      return {
        title: 'Delivery Mode Sales Performance',
        subtitle: 'Delivered qty by mode of delivery (m³)',
      };
    case 'vehicle':
      return {
        title: 'Vehicle-wise Sales Performance',
        subtitle: 'Delivered qty by vehicle (m³)',
      };
    case 'month':
      return {
        title: 'Month-wise Sales Performance',
        subtitle: `Apr–Mar · ${fyLabel || 'Financial year'} (m³)`,
      };
    case 'day':
      return {
        title: 'Day-wise Sales Performance',
        subtitle: 'Delivered qty by day & month (m³)',
      };
    default:
      return {
        title: 'Marketing-wise Sales Performance',
        subtitle: 'Delivered qty by CSM (m³)',
      };
  }
}

function MarketingSalesChart({
  salesData,
  cookies,
  plantId,
  referenceDate,
}) {
  const [view, setView] = useState('marketing');
  const [fyStartYear, setFyStartYear] = useState(() => getFinancialYearStartYear(referenceDate || moment()));
  const [fySalesData, setFySalesData] = useState([]);
  const [fyLoading, setFyLoading] = useState(false);
  const [fyError, setFyError] = useState('');
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const fyOptions = useMemo(
    () => buildFinancialYearOptions(referenceDate || moment()),
    [referenceDate],
  );

  useEffect(() => {
    const next = getFinancialYearStartYear(referenceDate || moment());
    setFyStartYear(next);
  }, [referenceDate]);

  const loadFySales = useCallback(() => {
    if (!cookies?.myToken || !plantId || view !== 'month') return;
    const { fyStart, fyEnd } = getFinancialYearBounds(fyStartYear);
    setFyLoading(true);
    setFyError('');
    getSalesMonthly(
      cookies,
      plantId,
      fyStart.format('DD-MM-YYYY'),
      fyEnd.format('DD-MM-YYYY'),
    )
      .then((response) => {
        setFySalesData(response?.monthly || []);
      })
      .catch((err) => {
        setFySalesData([]);
        setFyError(err?.response?.data?.detail || err?.message || 'Failed to load monthly sales');
      })
      .finally(() => setFyLoading(false));
  }, [cookies, plantId, view, fyStartYear]);

  useEffect(() => {
    loadFySales();
  }, [loadFySales]);

  const monthAgg = useMemo(() => {
    if (view !== 'month') return null;
    return aggregateMonthWiseByFinancialYear(fySalesData, fyStartYear);
  }, [view, fySalesData, fyStartYear]);

  const isTimeAxis = view === 'month' || view === 'day';
  const rows = useMemo(() => {
    if (view === 'month') return monthAgg?.rows || [];
    return aggregateSalesByView(salesData, view);
  }, [salesData, view, monthAgg]);

  const totalQty = useMemo(
    () => rows.reduce((sum, row) => sum + row.qty, 0),
    [rows],
  );
  const meta = getViewMeta(view, monthAgg?.fyLabel);

  useEffect(() => {
    if (!canvasRef.current) return undefined;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!rows.length) return undefined;

    const ctx = canvasRef.current.getContext('2d');
    const horizontal = !isTimeAxis;

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: rows.map((row) => row.name),
        datasets: [{
          label: 'Sales Qty (m³)',
          data: rows.map((row) => row.qty),
          backgroundColor: rows.map((_, index) => BAR_COLORS[index % BAR_COLORS.length]),
          borderRadius: 4,
          maxBarThickness: isTimeAxis ? 36 : 28,
        }],
      },
      options: {
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                if (view === 'month' && items[0]) {
                  const idx = items[0].dataIndex;
                  const key = rows[idx]?.sortKey;
                  return key ? moment(key, 'YYYY-MM').format('MMM YYYY') : items[0].label;
                }
                return items[0]?.label || '';
              },
              label: (item) => `${Number(item.raw || 0).toFixed(2)} m³`,
            },
          },
          datalabels: {
            anchor: 'end',
            align: horizontal ? 'right' : 'top',
            color: '#2c3e50',
            font: { size: 10, weight: '600' },
            formatter: (value) => (Number(value) > 0 ? Number(value).toFixed(1) : ''),
            clamp: true,
          },
        },
        scales: horizontal
          ? {
              x: {
                beginAtZero: true,
                grid: { color: 'rgba(0,0,0,0.05)' },
                ticks: {
                  font: { size: 10 },
                  callback: (value) => Number(value).toFixed(0),
                },
                title: { display: true, text: 'm³', font: { size: 11 } },
              },
              y: {
                grid: { display: false },
                ticks: { font: { size: 11 }, autoSkip: false },
              },
            }
          : {
              x: {
                grid: { display: false },
                ticks: {
                  font: { size: 10 },
                  maxRotation: view === 'day' ? 45 : 0,
                  minRotation: view === 'day' ? 45 : 0,
                  autoSkip: view === 'day',
                  maxTicksLimit: view === 'day' ? 16 : 12,
                },
                title: {
                  display: true,
                  text: view === 'day' ? 'Day & Month' : `Month (Apr–Mar · ${monthAgg?.fyLabel || 'FY'})`,
                  font: { size: 11 },
                },
              },
              y: {
                beginAtZero: true,
                grid: { color: 'rgba(0,0,0,0.05)' },
                ticks: {
                  font: { size: 10 },
                  precision: 0,
                  maxTicksLimit: 6,
                  callback: (value) => Number(value).toFixed(0),
                },
                title: { display: true, text: 'm³', font: { size: 11 } },
              },
            },
        layout: {
          padding: horizontal ? { right: 28 } : { top: 18, bottom: 4 },
        },
      },
      plugins: [ChartDataLabels],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [rows, isTimeAxis, view, monthAgg]);

  const canvasHeight = isTimeAxis
    ? 220
    : Math.max(140, Math.min(rows.length * 34 + 40, view === 'vehicle' ? 520 : 340));

  const emptyMessage = (() => {
    if (view === 'month' && !plantId) return 'Select a plant to load financial year sales.';
    if (view === 'month' && fyLoading) return 'Loading financial year sales…';
    if (view === 'month' && fyError) return fyError;
    if (view === 'month' && totalQty <= 0) {
      return `No sales in ${monthAgg?.fyLabel || 'this financial year'} for the selected plant.`;
    }
    return 'No sales data for this period.';
  })();

  return (
    <div className="marketing-sales-chart">
      <div className="marketing-sales-chart-header">
        <div>
          <strong>{meta.title}</strong>
          <span className="marketing-sales-chart-sub">{meta.subtitle}</span>
        </div>
        <div className="marketing-sales-chart-total">
          {fyLoading && view === 'month' ? '…' : totalQty.toFixed(2)} <small>m³</small>
        </div>
      </div>

      <div className="marketing-sales-chart-views" role="tablist" aria-label="Sales chart view">
        {VIEW_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={view === option.id}
            className={`marketing-sales-chart-view-btn ${view === option.id ? 'is-active' : ''}`}
            onClick={() => setView(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {view === 'month' && (
        <div className="marketing-sales-chart-fy">
          <label htmlFor="sales-chart-fy">Financial year</label>
          <select
            id="sales-chart-fy"
            className="marketing-sales-chart-fy-select"
            value={fyStartYear}
            onChange={(e) => setFyStartYear(Number(e.target.value))}
          >
            {fyOptions.map((option) => (
              <option key={option.startYear} value={option.startYear}>
                {option.label} (Apr–Mar)
              </option>
            ))}
          </select>
        </div>
      )}

      {!rows.length || (view === 'month' && fyLoading) ? (
        <p className="marketing-sales-chart-empty">{emptyMessage}</p>
      ) : (
        <div
          className="marketing-sales-chart-canvas-wrap"
          style={{ height: canvasHeight }}
        >
          <canvas ref={canvasRef} />
        </div>
      )}
      {view === 'month' && !fyLoading && fyError ? (
        <p className="marketing-sales-chart-empty">{fyError}</p>
      ) : null}
    </div>
  );
}

export default MarketingSalesChart;
